import { Bath, Plus, Trash2 } from "lucide-react";

import {
  aed,
  listPriceFor,
  packagePrice,
  ROOM_KIND_LABEL,
  type ExtraOptionRow,
  type OrderRoomInput,
  type OrderRoomRow,
  type OrderSection,
  type OrderSettingsRow,
  type OrderTotals,
  type PriceRow,
  type RoomKind,
} from "@/lib/orders";
import { RoomPhotos } from "./RoomPhotos";
import { SectionCard } from "./SectionCard";
import { BTN_GHOST, BTN_GOLD, Card } from "./ui";

const KINDS: RoomKind[] = ["custom", "bathroom_small", "bathroom_medium", "bathroom_large"];

/**
 * The rooms of a quotation. Each room sells a floor and/or a wall; a bathroom
 * package prices both as fixed amounts (Settings → Orders), extras on top.
 */
export function RoomsCard({
  rooms,
  onChange,
  priceList,
  extraOptions,
  settings,
  totals,
  disabled,
  orderId,
  savedRooms,
  onPhotosChanged,
}: {
  rooms: OrderRoomInput[];
  onChange: (rooms: OrderRoomInput[]) => void;
  priceList: PriceRow[];
  extraOptions: ExtraOptionRow[];
  settings: OrderSettingsRow;
  totals: OrderTotals | null;
  disabled: boolean;
  /** Saved order: photos hang off the saved rooms (matched by id). */
  orderId?: string | null;
  savedRooms?: OrderRoomRow[];
  onPhotosChanged?: () => void | Promise<void>;
}) {
  const firstFinish = priceList.find((p) => p.active) ?? priceList[0];
  const rate = (p: "floor" | "wall") => (firstFinish ? listPriceFor(settings, priceList, "microcement", firstFinish.finish, p) : 0);

  const patchRoom = (i: number, r: Partial<OrderRoomInput>) => onChange(rooms.map((x, k) => (k === i ? { ...x, ...r } : x)));
  const remove = (i: number) => onChange(rooms.filter((_, k) => k !== i));

  const addRoom = () => onChange([...rooms, { name: `Room ${rooms.length + 1}`, kind: "custom", floor: null, wall: null }]);
  const addBathroom = () => {
    const kind: RoomKind = "bathroom_small";
    const sec = (p: "floor" | "wall"): OrderSection | null =>
      firstFinish ? { system: "microcement" as const, finish: firstFinish.finish, sqm: 0, unitPrice: packagePrice(settings.bathroomPackages, kind, p) ?? rate(p), extras: [], colour: "" } : null;
    const n = rooms.filter((r) => r.kind !== "custom").length + 1;
    onChange([...rooms, { name: `Bathroom ${n}`, kind, floor: sec("floor"), wall: sec("wall") }]);
  };

  /** Switching kind re-prices the sections: package price in, list rate out. */
  const changeKind = (i: number, kind: RoomKind) => {
    const r = rooms[i];
    if (!r) return;
    const re = (s: OrderSection | null | undefined, p: "floor" | "wall") =>
      s ? { ...s, unitPrice: packagePrice(settings.bathroomPackages, kind, p) ?? listPriceFor(settings, priceList, s.system, s.finish, p) } : s;
    patchRoom(i, { kind, floor: re(r.floor, "floor"), wall: re(r.wall, "wall") });
  };

  return (
    <>
      {rooms.map((r, i) => {
        const t = totals?.rooms[i];
        const pkgFloor = packagePrice(settings.bathroomPackages, r.kind, "floor");
        const pkgWall = packagePrice(settings.bathroomPackages, r.kind, "wall");
        return (
          <Card
            key={i}
            tag={`2.${i + 1} · Room`}
            right={
              !disabled && rooms.length > 1 ? (
                <button type="button" className="rounded-full border border-white/15 bg-night/80 px-3 py-1 text-[0.62rem] uppercase tracking-[0.14em] text-ice/75 hover:text-pink" onClick={() => remove(i)}>
                  <Trash2 className="mr-1 inline h-3 w-3" /> Remove
                </button>
              ) : undefined
            }
          >
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[1fr_15rem_auto] xl:items-end">
              <label className="block">
                <span className="field-label">Room name</span>
                <input className="field" disabled={disabled} value={r.name} onChange={(e) => patchRoom(i, { name: e.target.value })} placeholder="Living room, Master bathroom…" />
              </label>
              <label className="block">
                <span className="field-label">Type</span>
                <select className="field" disabled={disabled} value={r.kind} onChange={(e) => changeKind(i, e.target.value as RoomKind)}>
                  {KINDS.map((k) => (
                    <option key={k} value={k} className="bg-night">{ROOM_KIND_LABEL[k]}</option>
                  ))}
                </select>
              </label>
              <div className="text-sm text-ice/60 sm:col-span-2 sm:text-right xl:col-span-1">
                Room total <b className="num text-lg text-goldbright">{aed(t?.total ?? 0)}</b>
              </div>
            </div>
            {r.kind !== "custom" && (
              <p className="mt-2 text-xs text-ice/60">
                <Bath className="mr-1 inline h-3 w-3" /> Package: floor <b className="text-paper">{aed(pkgFloor ?? 0)}</b> · wall <b className="text-paper">{aed(pkgWall ?? 0)}</b>. Extras apply on top; m² is optional and only feeds the project.
              </p>
            )}

            <SectionCard
              tag={`${i + 1}.1 · Floor`}
              kind="floor"
              section={r.floor ?? null}
              onChange={(s) => patchRoom(i, { floor: s })}
              priceList={priceList}
              extraOptions={extraOptions}
              settings={settings}
              disabled={disabled}
              base={t?.floor.base ?? 0}
              extrasAmt={t?.floor.extrasAmt ?? 0}
              fixedPrice={pkgFloor}
            />
            <SectionCard
              tag={`${i + 1}.2 · Wall`}
              kind="wall"
              section={r.wall ?? null}
              onChange={(s) => patchRoom(i, { wall: s })}
              priceList={priceList}
              extraOptions={extraOptions}
              settings={settings}
              disabled={disabled}
              base={t?.wall.base ?? 0}
              extrasAmt={t?.wall.extrasAmt ?? 0}
              fixedPrice={pkgWall}
            />
            {orderId !== undefined && (
              <RoomPhotos
                orderId={orderId}
                roomId={r.id}
                photos={savedRooms?.find((s) => s.id === r.id)?.photos ?? []}
                onChanged={() => onPhotosChanged?.()}
              />
            )}
          </Card>
        );
      })}

      {!disabled && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={BTN_GHOST} onClick={addRoom}>
            <Plus className="h-3.5 w-3.5" /> Add room
          </button>
          <button type="button" className={BTN_GOLD} onClick={addBathroom}>
            <Bath className="h-3.5 w-3.5" /> Add bathroom package
          </button>
        </div>
      )}
    </>
  );
}
